import { useLocation } from "@reach/router"
import ReactPlayerLoader from "@brightcove/react-player-loader"
import find from "lodash/find"
import get from "lodash/get"
import qs from "qs"
import React, { useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"
import tw from "twin.macro"
import useIsInViewport from "use-is-in-viewport"
import { cleanedText, getQueryStringValue } from "../util"
import getLocalizedString from "../utils/getLocalizedString"
import { imageItemTransformer } from "../utils/transformers"
import ThemeContext from "../context/ThemeContext"
import Button, { ButtonLink } from "./Button"
import Caption from "./Caption"
import Image from "./ImageOptim"
import Modal from "./Modal"
import { PageContext } from "./PageProvider"
import RichText from "./RichText"
import isChromatic from "chromatic/isChromatic"

/**
 * Videos will autoplay unless `isChromatic()` is true or the environment variable `GATSBY_DISABLE_VIDEO_AUTOPLAY` is
 * set to `1`. This works around Chromatic’s change to pause videos by which breaks the video component.
 */
const ALLOW_VIDEO_AUTOPLAY = !(
  isChromatic() || process.env.GATSBY_DISABLE_VIDEO_AUTOPLAY === "1"
)

export interface ResponsiveWrapperProps {
  /**
   * The classes applied to the wrapper. If the class `absolute` is not present, the wrapper will be `relative` by default.
   */
  className?: string
  /**
   * The ratio of the video represented as a decimal (e.g. 16:9 = 9/16 = 0.5625). Defaults to 0.5625 (16:9)
   */
  ratio?: number
  /**
   * If true, the video and poster will be set to `object-fit: cover;` and `object-position: 50% 50%;` and the player controls will be hidden.
   */
  cover?: boolean
}

const ResponsiveWrapper = styled.div<ResponsiveWrapperProps>`
  ${tw`w-full bg-gray-200`}
  ${(props) => {
    if (
      !props.className ||
      (props.className && props.className.indexOf(`absolute`) === -1)
    ) {
      return tw`relative`
    }
  }}
  ${(props) => {
    if (props.ratio) {
      return `padding-bottom: ${props.ratio * 100}%;`
    } else if (props.ratio === 0) {
      return
    } else {
      return `padding-bottom: 56.25%;`
    }
  }}
  .laitram-bc-player {
    ${tw`inset-0 absolute w-full h-full`}
    .video-js {
      ${tw`inset-0 absolute h-full w-full`}
    }
    .vjs-dock-text {
      ${tw`hidden`}
    }
  }
  ${(props) =>
    props.cover &&
    `
    .vjs-tech {
      object-fit: cover;
      object-position: 50% 50%;
    }
    .vjs-poster {
      background-size: cover;
    }

    /* Hide the player control bar */
    .video-js .vjs-control-bar {
      display: none;
    }

    /* Remove click events on the player */
    .video-js {
      pointer-events: none;
    }
  `}
`

const CoverImage = styled.div`
  ${tw`inset-0 absolute h-full w-full cursor-pointer bg-black`}
  img {
    ${tw`w-full h-full object-cover block`}
    ${(props) => {
      if (props.cover) {
        return tw`absolute inset-0`
      }
    }}
    height: 100% !important;
    transition: opacity 300ms ease-in-out;
  }
  &:hover img {
    opacity: 0.88;
  }
  .laitram-bc-cover {
    ${tw`inset-0 absolute h-full w-full`}
  }
`

export interface PlayIconProps {
  /**
   * The size of the icon. Defaults to `base`.
   */
  iconSize?: "sm" | "base"
}

const PlayIcon = styled.button<PlayIconProps>`
  ${tw`absolute rounded-full w-24 h-24 text-white border-0 focus:outline-none focus:ring`}
  ${(props) => (props.iconSize === "sm" ? tw`w-12 h-12` : tw`w-24 h-24`)}
  color: rgba(255,255,255, .8);
  background-color: rgba(0, 0, 0, 0.5);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: color 300ms ease-in-out;
  i {
    ${tw`absolute`}
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    ${(props) =>
      props.iconSize === "sm" ? `font-size: 16px` : `font-size: 32px;`}
  }
  ${CoverImage}:hover & {
    color: rgba(255, 255, 255, 1);
  }
`

export interface VideoMarkupProps {
  /**
   * Provides attributes (props) to the component element.
   */
  attrs?: any
  /**
   * The Brightcove video ID.
   */
  videoId: string
  /**
   * A callback function that is called when the video is successfully loaded.
   */
  onSuccess?: (success: any) => void
}

const VideoMarkup = ({ attrs, onSuccess, videoId }: VideoMarkupProps) => {
  const { autoplay, loop, ...otherAttrs } = attrs

  return (
    <ReactPlayerLoader
      accountId={process.env.GATSBY_BRIGHTCOVE_ACCOUNT_ID}
      playerId={process.env.GATSBY_BRIGHTCOVE_PLAYER_ID}
      videoId={videoId}
      attrs={{ className: "laitram-bc-player" }}
      onSuccess={onSuccess}
      options={{
        ...otherAttrs,
        autoplay: ALLOW_VIDEO_AUTOPLAY ? autoplay : undefined,
        loop: ALLOW_VIDEO_AUTOPLAY ? loop : undefined,
      }}
    />
  )
}

export interface VideoProps {
  /**
   * Classes applied to the responsive wrapper. Useful to set the wrapper to `absolute` if the video is to be used as a background
   */
  className?: string
  /**
   * **Deprecated** The color scheme of the video. Defaults to `light`.
   * @deprecated Video color schemes are no longer supported.
   */
  colorScheme?: "default" | "light" | "dark"
  /**
   * Used when displaying a button to play the video in a modal. Set to true if the button should be light to contrast with a dark background.
   */
  contrast?: boolean
  /**
   * If true, the video and poster will be set to `object-fit: cover;` and `object-position: 50% 50%;` and the player controls will be hidden.
   */
  cover?: boolean
  /**
   * The width of the container the video is displayed in. Used to optimize the cover image.
   */
  coverImageCropWidth?: number
  /**
   * The variant of the component to display. Defaults to `video`. If format is `link` or `button`, `playInModal` must be true for component to function as expected.
   */
  format?: "link" | "button" | "video"
  /**
   * The size of the icon. Defaults to `base`.
   */
  iconSize?: "sm" | "base"
  /**
   * The label for the link or button. Defaults to `Watch Video`.
   */
  linkLabel?: string
  /**
   * If true, the video will play in a modal. When using `link` or `button` format, `playInModal` must be true for component to function as expected
   */
  playInModal?: boolean
  /**
   * The ratio of the video represented as a decimal (e.g. 16:9 = 9/16 = 0.5625). Defaults to 0.5625 (16:9)
   */
  ratio?: number
  /**
   * If true, the caption will not be displayed.
   */
  suppressCaption?: boolean
  /**
   * The data from a Video item in Kontent
   */
  video: any
  /**
   * Provides attributes (props) like autoplay and playsinline to the component element.
   * TODO: Determine the proper type (e.g. HTMLProps<HTMLVideoElement>, HTMLAttributes<HTMLVideoElement>, etc)
   */
  attrs?: any
}

/**
 * Display a video with all of the necessary containers and features for the site.
 */
const Video = ({
  className,
  colorScheme,
  contrast,
  cover,
  coverImageCropWidth,
  format,
  iconSize,
  linkLabel,
  playInModal,
  ratio,
  suppressCaption = false,
  video,
  ...attrs
}: VideoProps) => {
  const title = get(video, "elements.title.value")
  const altText = get(video, "elements.alt_text.value") || title
  const brightcoveId = get(video, "elements.brightcove_id.value")
  const caption = cleanedText(get(video, "elements.caption.value"))
  const captionLinkedItems = get(video, "elements.caption.modular_content")
  const captionLinks = get(video, "elements.caption.links")
  const coverImage = get(video, "elements.cover_image.value[0]")
  const urlSlug = get(video, "elements.url_slug.value")
  const options = get(video, "elements.options.value") || []
  const autoplay =
    find(options, (option) => get(option, "codename") === "autoplay") && true
  const loop =
    find(options, (option) => get(option, "codename") === "loop") && true
  const [isInViewport, targetRef] = useIsInViewport({
    threshold: 75,
  })
  const [showModal, setShowModal] = useState(false)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [player, setPlayer] = useState()
  const [play, setPlay] = useState(false)
  const [isAutoPaused, setIsAutoPaused] = useState(false)

  const { hydrated: isClient } = React.useContext(ThemeContext)
  const location = useLocation()
  const urlSearchParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  const videoParam = getQueryStringValue("video")

  const handleUrlChange = useCallback(() => {
    if (videoParam === urlSlug && !showModal) {
      setShowModal(true)
    } else if (videoParam !== urlSlug && showModal) {
      setShowModal(false)
    }
  }, [videoParam, showModal, urlSlug])

  //Allows the modal to open when page initially loads
  if (urlSlug) {
    handleUrlChange()
  }

  const ctx = useContext(PageContext)
  const localizedStrings = ctx.localizedStrings
  const localizedWatchVideo = getLocalizedString(
    localizedStrings,
    "localization_string___watch_video",
    "Watch Video"
  )

  if (autoplay && Object.keys(attrs || {}).length) {
    attrs.playsinline = true
  }

  const handleCoverImageClick = () => {
    if (playInModal) {
      handleModalChange(true, true)
    } else {
      player.play()
    }
  }

  /**
   * Open or close the modal and play or pause the video
   * @param {boolean} show - indicates whether the modal should be shown
   * @param {boolean} play - indicates whether the video should play
   */
  const handleModalChange = (show, play) => {
    // Updates the URL search params with the modal that is open (and removes it when closed)
    if (show) {
      if (urlSlug) {
        // Adds video=${url_slug} param to URL
        urlSearchParams.video = urlSlug
        window.history.pushState(
          {},
          ``,
          `${location.pathname}?${qs.stringify(urlSearchParams)}`
        )
      }
      if (play) {
        setPlay(true)
      }
    } else {
      // Removes video=${url_slug} param from URL
      if (urlSlug) {
        window.history.pushState({}, ``, location.pathname)
      }
      setPlay(false)
    }

    setShowModal(show)
  }

  // - Do we have a player element
  // - Is the video autoplay?
  // - If it's not autoplay, is it visible and has the user already pressed play on it?
  useEffect(() => {
    if (player) {
      if (autoplay && ALLOW_VIDEO_AUTOPLAY) {
        if (isInViewport) {
          player.muted(true)
          player.play()
          player.controls(false)
          loop && player.loop(true)
        } else {
          player.pause()
        }
      } else {
        if (!playInModal) {
          if (isInViewport && !isPlaying && isAutoPaused) {
            player.play()
          } else if (!isInViewport && isPlaying) {
            player.pause()
            setIsAutoPaused(true)
          }
        }
      }
    }
  }, [
    autoplay,
    isAutoPaused,
    isInViewport,
    isPlaying,
    loop,
    player,
    playInModal,
  ])

  useEffect(() => {
    if (!isClient) {
      return
    }

    const popStateListener = (event) => {
      handleUrlChange()
    }

    if (urlSlug) {
      // Updates visibility of the modal when the URL changes
      window.addEventListener("popstate", popStateListener)
    }

    return function cleanup() {
      if (urlSlug) {
        window.removeEventListener("popstate", popStateListener)
      }
    }
  }, [isClient, handleUrlChange, urlSlug])

  const onBrightcoveSuccess = (success) => {
    // https://docs.brightcove.com/brightcove-player/1.x/Player.htm
    const player = success.ref

    /** The success function does not mean the video can be interacted with
     * programmatically. Brightcove recommends using the `loadedmetadata` event
     * @see https://player.support.brightcove.com/coding-topics/player-events.html#:~:text=ready()%20vs.%20on(%27loadedmetadata%27%2C...)
     **/
    player.on("loadedmetadata", () => {
      setPlayer(player)
    })

    const handlePlay = () => {
      setIsPlaying(true)
      setIsPaused(false)
      setIsAutoPaused(false)
    }
    const handlePause = () => {
      setIsPlaying(false)
      setIsPaused(true)
    }
    const handleEnded = () => {
      setIsPlaying(false)
    }

    success.ref.on("play", handlePlay)
    success.ref.on("pause", handlePause)
    success.ref.on("ended", handleEnded)

    if (cover && isClient) {
      const videoEl = success.ref.el().querySelector(".vjs-tech")
      videoEl.dataset.objectFit = "cover"
      window.objectFitPolyfill(videoEl) // eslint-disable-line
    }

    /**
     * This is a workaround for the video not autoplaying when a modal is opened
     */
    if (play === true) {
      try {
        success.ref.play()
      } catch (e) {
        console.log(e)
      }
    }

    if (play === false) {
      try {
        success.ref.pause()
      } catch (e) {
        console.log(e)
      }
    }
  }

  const ButtonComponent = format === `button` ? Button : ButtonLink

  return (
    <>
      {format === `link` || format === `button` ? (
        <ButtonComponent
          colorScheme={`default`}
          iconColorScheme={contrast ? `contrast` : `default`}
          contrast={contrast}
          onClick={(event) => {
            handleModalChange(true, true)
            event.preventDefault()
          }}
          {...{
            "data-analytics-title": get(video, "system.name"),
            "data-analytics-category": "Video",
          }}
          {...(format === `button`
            ? {
                as: `button`,
                buttonType: `pill`,
              }
            : {})}
        >
          <span className="flex">
            <i
              className="icon-play-circle mr-1"
              style={{ lineHeight: "inherit" }}
            ></i>
            {linkLabel || localizedWatchVideo}
          </span>
        </ButtonComponent>
      ) : (
        <>
          <ResponsiveWrapper
            cover={cover}
            ref={targetRef}
            ratio={ratio}
            className={className}
          >
            {!playInModal && isClient ? (
              <VideoMarkup
                attrs={attrs}
                onSuccess={onBrightcoveSuccess}
                videoId={brightcoveId}
              />
            ) : null}
            {(coverImage && !isPlaying && !isPaused) || playInModal ? (
              <CoverImage
                onClick={handleCoverImageClick}
                cover={cover}
                {...{
                  "data-analytics-title": get(video, "system.name"),
                  "data-analytics-category": "Video",
                }}
              >
                {coverImage ? (
                  <Image
                    {...imageItemTransformer(coverImage)}
                    cropRatio={ratio ? ratio : 0.5625}
                    cropWidth={coverImageCropWidth}
                    cover={cover}
                  />
                ) : null}
                <PlayIcon
                  aria-label={localizedWatchVideo}
                  role="button"
                  tabIndex="0"
                  iconSize={iconSize}
                >
                  <i className="icon-play" aria-hidden></i>
                </PlayIcon>
              </CoverImage>
            ) : null}
          </ResponsiveWrapper>
          {caption && !suppressCaption ? (
            <Caption className={`text-left`}>
              <RichText
                content={caption}
                linkedItems={captionLinkedItems}
                links={captionLinks}
                spacing="xs"
              />
            </Caption>
          ) : null}
        </>
      )}
      {playInModal ? (
        <Modal
          isOpen={showModal === true}
          onChange={handleModalChange}
          ariaLabel={altText || "Video"}
        >
          <ResponsiveWrapper>
            {isClient ? (
              <VideoMarkup
                attrs={attrs}
                onSuccess={onBrightcoveSuccess}
                videoId={brightcoveId}
              />
            ) : null}
          </ResponsiveWrapper>
        </Modal>
      ) : null}
    </>
  )
}

export default Video
